import { BUILDING_PLANS, buildingLevel } from './buildingmaps';
import { buildTerrain } from './levels';
import { UNIT_H, UNIT_HALF_W } from './physics';

/**
 * Four to eight teams on a building map: every team starts on a floor of its own (there are nine
 * of them), spread so that neighbours are not stacked: the two ground floors at the ends first,
 * then the hall's top and ground floors, then the tower tops, then the middle floors. Each entry
 * is a (building, floor) pair into the map's plan, floor 0 being the top.
 */
const FLOOR_ORDER: [number, number][] = [
  [0, 2], [2, 2], [1, 0], [1, 2], [0, 0], [2, 0], [1, 1], [0, 1], [2, 1],
];

/** Standing room at (x, y): ground within 3 px under the feet and a mole's body clear above. */
function stands(solid: (x: number, y: number) => boolean, x: number, y: number): boolean {
  if (!(solid(x, y + 1) || solid(x, y + 2) || solid(x, y + 3))) return false;
  if (solid(x, y)) return false;
  for (let dy = 6; dy < UNIT_H; dy += 8) if (solid(x - UNIT_HALF_W * 0.6, y - dy) || solid(x + UNIT_HALF_W * 0.6, y - dy)) return false;
  return true;
}

/**
 * The spawn lists for `teams` teams of `perTeam` moles on a building map, one floor per team, or
 * null when the map is not a building map or has too few floors to give each team its own.
 */
export function buildingFloorSpawns(levelId: string, teams: number, perTeam: number): [number, number][][] | null {
  const plan = BUILDING_PLANS[levelId];
  const level = buildingLevel(levelId);
  if (!plan || !level || teams > FLOOR_ORDER.length) return null;
  const t = buildTerrain(level);
  const solid = (x: number, y: number): boolean => t.isSolid(x, y);
  const out: [number, number][][] = [];
  for (let i = 0; i < teams; i++) {
    const [bi, fi] = FLOOR_ORDER[i]!;
    const floor = plan[bi]?.floors[fi];
    if (!floor) return null;
    // The feet row the floor really has, within a few pixels of the plan.
    const mid = Math.round((floor.x0 + floor.x1) / 2);
    let feet: number | null = null;
    for (const dy of [0, -1, -2, 1, 2, 3]) {
      if (stands(solid, mid, floor.y + dy)) {
        feet = floor.y + dy;
        break;
      }
    }
    if (feet === null) return null;
    // Spots spread out from the middle of the floor, at least 48 px apart, on standing room only.
    const spots: [number, number][] = [];
    for (let k = 0; spots.length < perTeam && k < 64; k++) {
      const x = mid + (k % 2 === 0 ? 1 : -1) * Math.ceil(k / 2) * 48;
      if (x < floor.x0 + 16 || x > floor.x1 - 16) continue;
      if (stands(solid, x, feet)) spots.push([x, feet]);
    }
    if (spots.length < perTeam) return null;
    // Innermost first: nearest the middle of the map.
    spots.sort((a, b) => Math.abs(a[0] - level.width / 2) - Math.abs(b[0] - level.width / 2));
    out.push(spots);
  }
  return out;
}
